/**
 * useLabelFilterState Hook
 *
 * URL-synchronized state management for label filtering.
 * Stores filter state in URL params (?filter=labelId1,labelId2) for:
 * - Shareable links with filters applied
 * - Browser back/forward navigation
 * - Cross-route persistence (mindmap <-> report page)
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const FILTER_PARAM_KEY = 'filter'

interface UseLabelFilterStateOptions {
  /** Mindmap ID for scoping filters - clears filters when this changes */
  mindmapId: string | undefined
  /** Whether to sync state to URL params (default: true) */
  syncToUrl?: boolean
}

interface UseLabelFilterStateReturn {
  /** Currently selected filter label IDs */
  selectedFilterLabels: string[]
  /** Set the selected filter labels */
  setSelectedFilterLabels: (labels: string[]) => void
  /** Clear all filter labels */
  clearFilterLabels: () => void
  /** Toggle a single filter label */
  toggleFilterLabel: (labelId: string) => void
  /** Whether any filter is active */
  hasActiveFilter: boolean
}

/**
 * Parse filter param from URL
 */
function parseFilterParam(param: string | null): string[] {
  if (!param) return []
  return param.split(',').filter((id) => id.trim().length > 0)
}

/**
 * Serialize filter labels to URL param
 */
function serializeFilterParam(labels: string[]): string {
  return labels.join(',')
}

/**
 * Hook for managing label filter state with URL synchronization.
 *
 * @example
 * ```tsx
 * const { selectedFilterLabels, setSelectedFilterLabels, clearFilterLabels } =
 *   useLabelFilterState({ mindmapId })
 * ```
 */
export function useLabelFilterState({
  mindmapId,
  syncToUrl = true,
}: UseLabelFilterStateOptions): UseLabelFilterStateReturn {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize state from URL — captured once on mount via ref
  const initialLabels = useRef(
    syncToUrl ? parseFilterParam(searchParams.get(FILTER_PARAM_KEY)) : []
  ).current

  const [selectedFilterLabels, setSelectedFilterLabelsInternal] = useState<string[]>(initialLabels)

  // Mirror selectedFilterLabels into a ref so the sync effect can read it without depending on it
  const selectedFilterLabelsRef = useRef(selectedFilterLabels)
  useEffect(() => {
    selectedFilterLabelsRef.current = selectedFilterLabels
  })

  // Track mindmapId to clear filters when it changes
  const [prevMindmapId, setPrevMindmapId] = useState<string | undefined>(mindmapId)

  // Clear filters when mindmap changes
  useEffect(() => {
    if (mindmapId !== prevMindmapId) {
      setPrevMindmapId(mindmapId)
      // Only clear if we're switching to a different mindmap (not initial load)
      if (prevMindmapId !== undefined && mindmapId !== undefined) {
        setSelectedFilterLabelsInternal([])
        if (syncToUrl) {
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev)
              newParams.delete(FILTER_PARAM_KEY)
              return newParams
            },
            { replace: true }
          )
        }
      }
    }
  }, [mindmapId, prevMindmapId, syncToUrl, setSearchParams])

  // Sync URL changes to state (for browser back/forward)
  useEffect(() => {
    if (!syncToUrl) return

    const urlLabels = parseFilterParam(searchParams.get(FILTER_PARAM_KEY))
    const currentSerialized = serializeFilterParam(selectedFilterLabelsRef.current)
    const urlSerialized = serializeFilterParam(urlLabels)

    if (currentSerialized !== urlSerialized) {
      setSelectedFilterLabelsInternal(urlLabels)
    }
  }, [searchParams, syncToUrl])

  const setSelectedFilterLabels = useCallback(
    (labels: string[]) => {
      setSelectedFilterLabelsInternal(labels)

      if (syncToUrl) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev)
            if (labels.length > 0) {
              newParams.set(FILTER_PARAM_KEY, serializeFilterParam(labels))
            } else {
              newParams.delete(FILTER_PARAM_KEY)
            }
            return newParams
          },
          { replace: true }
        )
      }
    },
    [syncToUrl, setSearchParams]
  )

  const clearFilterLabels = useCallback(() => {
    setSelectedFilterLabels([])
  }, [setSelectedFilterLabels])

  const toggleFilterLabel = useCallback(
    (labelId: string) => {
      setSelectedFilterLabels(
        selectedFilterLabels.includes(labelId)
          ? selectedFilterLabels.filter((id) => id !== labelId)
          : [...selectedFilterLabels, labelId]
      )
    },
    [selectedFilterLabels, setSelectedFilterLabels]
  )

  const hasActiveFilter = selectedFilterLabels.length > 0

  return {
    selectedFilterLabels,
    setSelectedFilterLabels,
    clearFilterLabels,
    toggleFilterLabel,
    hasActiveFilter,
  }
}
